import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import DeleteButton from '../../partials/actions/DeleteButton';
import DateSelect from '../../components/DateSelect';
import FilterButton from '../../components/DropdownFilter';
import ResultsTable from '../../partials/vulns/ResultsTable';
import PaginationClassic from '../../components/PaginationClassic';
import { Chart as ChartJS, ArcElement, DoughnutController, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";

// DoughnutController has to be registered here, not assumed. `overrides.doughnut`
// only exists once it is, and this module reads it at import time — so leaving
// registration to whoever happened to be imported first made the whole app's
// startup depend on the order of unrelated imports in the route table. It blanked
// the page the first time that order changed.
ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);

// Chart-level options rather than ChartJS.overrides: this module is imported for
// a route that is currently commented out, and a global mutation from it would
// restyle every doughnut in the platform.
const DOUGHNUT_OPTIONS = {
  cutout: '70%',
  plugins: { legend: { position: 'right' } },
};

function ScanResults() {

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const { id } = useParams();
  const location = useLocation();
  const scan = location.state.scan;
  console.log(scan);
  const countBy = (arr, prop) => arr.reduce((prev, curr) => (prev[curr[prop]] = ++prev[curr[prop]] || 1, prev), {});
  
  const generateColors = (numColors) => {
    const colors = [];
    for (let i = 0; i < numColors; i++) {
      const r = Math.floor(Math.random() * 255);
      const g = Math.floor(Math.random() * 255);
      const b = Math.floor(Math.random() * 255);
      colors.push(`rgba(${r}, ${g}, ${b}, 0.8)`);
    }
    return colors;
  };

  const osLabels = scan.res
    .filter(res => res.os !== null)
    .map(res => res.os);
  const osColors = generateColors(osLabels.length);

  const osData = {
    labels: osLabels,
    datasets: [{
      label: "OS",
      data: Object.values(countBy(scan.res, 'os')),
      backgroundColor: osColors,
      borderColor: osColors.map(color => color.replace('0.8', '1')),
      borderWidth: 1,
    }]
  };

  const serviceNames = scan.res.flatMap(res => 
    Array.isArray(res.ports) 
      ? res.ports.map(port => port.service['@name']) 
      : res.ports && typeof res.ports === 'object' 
        ? [res.ports.service['@name']] 
        : []
  );
  const serviceColors = generateColors(serviceNames.length);

  const servicesData = {
    labels: serviceNames,
    datasets: [{
      label: "Services",
      data: Object.values(countBy(serviceNames.map(name => ({ '@name': name })), '@name')),
      backgroundColor: serviceColors,
      borderColor: serviceColors.map(color => color.replace('0.2', '1')),
      borderWidth: 1,
    }]
  };

  const portProto = scan.res.flatMap(res => 
    Array.isArray(res.ports) 
      ? res.ports.map(port => port['@portid'] + '/' + port['@protocol']) 
      : res.ports && typeof res.ports === 'object' 
        ? [res.ports['@portid'] + '/' + res.ports['@protocol']]
        : []
  );
  const portColors = generateColors(portProto.length);

  const portData = {
    labels: portProto,
    datasets: [{
      label: "Ports/Protocols",
      data: Object.values(countBy(portProto.map(id => ({ '@portid': id })), '@portid')),
      backgroundColor: portColors,
      borderColor: portColors.map(color => color.replace('0.2', '1')),
      borderWidth: 1,
    }]
  };
  
  const handleSelectedItems = (selectedItems) => {
    setSelectedItems([...selectedItems]);
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">

      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">

        {/*  Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">

            {/* Page header */}
            <div className="sm:justify-between sm:items-center mb-8">

              {/* Left: Title */}
              <div className="mb-4 sm:mb-0">
                <div class="w-full p-3 rounded-sm text bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <div class="flex h-full">
                      <svg class="w-4 h-4 shrink-0 fill-indigo-500 mt-[3px] mr-3" viewBox="0 0 16 16">
                        <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zM8 6c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"></path>
                      </svg>
                    <div class="flex flex-col h-full">
                      <div class="grid grid-cols-2 gap-2">
                        <div class="font-semibold text-indigo-500 text-lg">{scan.meta.name}</div>
                        <div class="font-semibold text-sm mb-1">Started: {new Date(scan.meta.started).toLocaleString()}</div>
                        <div class="font-semibold text-sm mb-1">{scan.meta.type === '0' ? 'Discovery Scan' : scan.meta.type === '1' ? 'Deep Discovery Scan' : scan.meta.type === '2' ? 'Vulnerability Scan' : 'Web Scan'}</div>
                        <div class="font-semibold text-sm mb-1">Total assets: {scan.res.length}</div>
                      </div>  
                    </div>
                  </div>
                </div>
              </div>
              { (scan.meta.type === '0' || scan.meta.type === '1') && (
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">Operating Systems</h2>
                  </header>
                  <div className="p-4">    
                    <Doughnut data={osData} options={DOUGHNUT_OPTIONS} width={260} height={260} />
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">Network Services</h2>
                  </header> 
                  <div className="p-4">       
                    <Doughnut data={servicesData} options={DOUGHNUT_OPTIONS} width={260} height={260}/>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">Ports/Protocols</h2>
                  </header> 
                  <div className="p-4">       
                    <Doughnut data={portData} options={DOUGHNUT_OPTIONS} width={260} height={260}/>
                  </div>
                </div>
              </div>
              )}

              { scan.meta.type === '2' && (
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">CVEs</h2>
                  </header>
                  <div className="p-4">    
                    <Doughnut data={osData} options={DOUGHNUT_OPTIONS} width={260} height={260} />
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">Severity</h2>
                  </header> 
                  <div className="p-4">       
                    <Doughnut data={servicesData} options={DOUGHNUT_OPTIONS} width={260} height={260}/>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                  <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center space-x-16">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100">Highest Risk Asset</h2>
                  </header> 
                  <div className="p-4">       
                    <Doughnut data={servicesData} options={DOUGHNUT_OPTIONS} width={260} height={260}/>
                  </div>
                </div>
              </div>
              )}

              {/*<div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                <DeleteButton selectedItems={selectedItems} />
                <DateSelect />
                <FilterButton align="right" />
                <button className="btn bg-indigo-500 hover:bg-indigo-600 text-white">
                  <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                  </svg>
                  <span className="hidden xs:block ml-2">Add Order</span>
                </button>
              </div> */}

            </div>

            {/* Table */}
            <ResultsTable scan={scan} selectedItems={handleSelectedItems} />

            {/* Pagination */}
            <div className="mt-8">
              <PaginationClassic items={scan.res} />
            </div>

          </div>
        </main>

      </div>

    </div>
  );
}

export default ScanResults;